import * as React from 'react';
import { useState, useMemo } from 'react';

import { Modal } from 'components/bootstrap';
import { Icon } from 'components/common';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import useThreatRelatedIlluminatePacks from 'security-app/components/common/ThreatCoverageWidget/ImproveCoverageModal/useThreatRelatedIlluminatePacks';
import PacksInstallStatus from 'security-app/components/common/ThreatCoverageWidget/PacksInstallStatus';

import StepNavigation from './StepNavigation';
import type { StepKey } from './StepsOverview';
import StepsOverview, { STEPS } from './StepsOverview';
import IlluminatePacksEnable from './IlluminatePacksEnable';
import SigmaRulesEnable from './SigmaRulesEnable';

import type { CoverageEntity } from '../types';

type Props = {
  onHide: () => void;
  coverageEntity?: CoverageEntity;
  isTactic?: boolean;
  initialStep?: StepKey;
};

export type IlluminatePacksAreEnabled = {
  amountAllPacks: number;
  amountNotEnabledPacks: number;
  isIlluminatePacksAreEnabled: boolean;
  isLoadingPacks: boolean;
};
const useIlluminatePacksAreEnabled = (): IlluminatePacksAreEnabled => {
  const { allPacks, notEnabledPacks, isInitialLoading } = useThreatRelatedIlluminatePacks();

  return {
    amountAllPacks: allPacks.length,
    amountNotEnabledPacks: notEnabledPacks.length,
    isIlluminatePacksAreEnabled: allPacks.length > 0 && notEnabledPacks.length === 0,
    isLoadingPacks: isInitialLoading,
  };
};
const ImproveCoverageModal = ({
  onHide,
  coverageEntity = undefined,
  isTactic = undefined,
  initialStep = STEPS.overview.key,
}: Props) => {
  const [step, setStep] = useState<StepKey>(initialStep);
  const illuminatePacksAreEnabled = useIlluminatePacksAreEnabled();
  const sendTelemetry = useSendTelemetry();

  const isSigmaRulesStepAvailable = useMemo(
    () =>
      !illuminatePacksAreEnabled.isLoadingPacks &&
      illuminatePacksAreEnabled.amountNotEnabledPacks !== illuminatePacksAreEnabled.amountAllPacks,
    [
      illuminatePacksAreEnabled.amountAllPacks,
      illuminatePacksAreEnabled.amountNotEnabledPacks,
      illuminatePacksAreEnabled.isLoadingPacks,
    ],
  );

  const onNextClicked = (nextStep: StepKey) => {
    setStep(nextStep);
    sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP.THREAT_COVERAGE.IMPROVE_COVERAGE_STEP_NEXT, {
      app_pathname: 'security',
      app_section: 'threat-coverage',
      event_details: { step: nextStep },
    });
  };

  return (
    <Modal show onHide={onHide} bsSize="large">
      <Modal.Header closeButton>
        <Modal.Title>{STEPS[step].title(coverageEntity, isTactic)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <PacksInstallStatus>
          {step === 'overview' && (
            <StepsOverview
              illuminatePacksAreEnabled={illuminatePacksAreEnabled}
              setStep={setStep}
              coverageEntity={coverageEntity}
              isTactic={isTactic}
            />
          )}
          {step === STEPS.illuminatePacksEnable.key && (
            <>
              <StepNavigation
                setStep={setStep}
                nextButtonText={
                  <>
                    Enable Sigma Rules <Icon name="arrow_right_alt" />
                  </>
                }
                onClickNext={isSigmaRulesStepAvailable ? () => onNextClicked(STEPS.sigmaRulesEnable.key) : undefined}
              />
              <IlluminatePacksEnable />
            </>
          )}
          {step === STEPS.sigmaRulesEnable.key && (
            <>
              <StepNavigation setStep={setStep} nextButtonText="Close modal" onClickNext={onHide} />
              <SigmaRulesEnable coverageEntity={coverageEntity} />
            </>
          )}
        </PacksInstallStatus>
      </Modal.Body>
    </Modal>
  );
};

export default ImproveCoverageModal;
